import React from "react";
import { Radio, Mic, Globe2, Zap, Crown, TrendingUp, Megaphone, Sparkles, Coins, Check, BarChart3 } from "lucide-react";

/**
 * A5 brochure (148 × 210 mm). Two variants share this layout, differentiated by
 * the `variant` prop. Designed to render at exactly 1240 × 1748 px so a
 * Playwright screenshot at that viewport produces a print-ready 300 DPI PNG.
 *
 *  Variant "artist"    — emotional pitch for musicians / podcasters / DJs
 *  Variant "business"  — ROI pitch for local businesses / brands
 */
export default function BrochureA5({ variant = "artist" }) {
  const isArtist = variant === "artist";

  const HERO = isArtist
    ? {
        eyebrow: "FLIBOE FM · FOR ARTISTS, DJs & PODCASTERS",
        title: "Your station. Your mic. Your money.",
        sub: "Launch a 24/7 internet radio station the world can tune into — with AI behind the scenes, your face out front, and 100% of tips going straight to you.",
        accent: "from-emerald-400 to-fuchsia-500",
      }
    : {
        eyebrow: "FLIBOE FM · FOR LOCAL BUSINESSES & BRANDS",
        title: "Be heard everywhere. Be found online.",
        sub: "On-air audio sponsorships across every Fliboe station + AI-written SEO articles that rank you on Google, Bing and ChatGPT. Zero ad agencies. Zero retainers.",
        accent: "from-emerald-400 to-emerald-400",
      };

  const PLATFORM_FEATURES = isArtist
    ? [
        { icon: Mic, title: "AI Auto-DJ 24/7", body: "Claude writes DJ drops, OpenAI HD TTS voices them. Jokes, news, weather between songs." },
        { icon: Globe2, title: "Worldwide sync", body: "Every listener on every continent hears the same second of the same song." },
        { icon: Coins, title: "100% of listener tips", body: "Tip Jar runs on YOUR PayPal / CashApp / Venmo. Fliboe takes ZERO. Patreon = 8%, Twitch = 50%." },
        { icon: Sparkles, title: "AI-produced shows", body: "Pro narrated audio shows on demand. $25 (3-min) / $35 (5-min). Delivered in 30 seconds." },
        { icon: Zap, title: "One-click clip maker", body: "Export 9:16 portrait videos of any song with your station + song artwork for TikTok / Reels." },
        { icon: TrendingUp, title: "Listener demographics", body: "Live age / gender / location panel updates every 30s in your Studio." },
      ]
    : [
        { icon: Megaphone, title: "On-air audio ads", body: "Sponsor a 30-second slot that plays every 6 songs across every Fliboe station — for 7 days." },
        { icon: Globe2, title: "Worldwide reach", body: "Listeners on 6 continents. Your spot airs in their living room, car, gym, café." },
        { icon: BarChart3, title: "Real-time analytics", body: "Live age / gender / location panels by station so you know exactly who heard your spot." },
        { icon: Sparkles, title: "AI-produced sponsorship show", body: "Tell us about your business — we narrate a 3-5 min branded show in your category. Bundle with on-air ads." },
        { icon: Mic, title: "Built by a 20-year ad veteran", body: "Founder DJ Anup spent two decades in agencies. Fliboe is built for advertisers who want a fair deal." },
        { icon: Crown, title: "First-of-its-kind reach", body: "Direct line to real human listeners worldwide. No bot traffic. No Google middleman. No banner blindness." },
      ];

  const PULSE_BLOCK = isArtist
    ? {
        title: "Pulse · Your SEO blog. On autopilot.",
        body: "Pulse writes long-form articles about your station and music using Claude Sonnet 4.5 + Gemini Nano Banana cover art. Designed to rank on Google, Bing AND ChatGPT / Perplexity. Bottom of every post links back to your station's live stream — SEO becomes listeners.",
      }
    : {
        title: "Pulse · Get found by buyers searching for you.",
        body: "Pulse writes long-form articles about your business using Claude Sonnet 4.5 + Nano Banana cover art. Designed to rank on Google, Bing AND ChatGPT / Perplexity. Embed the resulting blog on your own site with a single line of code, or send traffic to the hosted /p/your-slug page.",
      };

  const PULSE_TIERS = [
    { name: "Starter", price: "$19", per: "/mo", line1: "4 posts/mo", line2: "1 site" },
    { name: "Pro",     price: "$49", per: "/mo", line1: "20 posts/mo", line2: "3 sites", featured: true },
    { name: "Agency",  price: "$149", per: "/mo", line1: "Unlimited posts", line2: "10 sites · white-label" },
  ];

  const PLANS = isArtist
    ? [
        { name: "Free Listener", price: "$0",     per: "/mo", bullets: ["Stream all stations worldwide", "Save favorites", "PWA install on iOS/Android"] },
        { name: "Premium",        price: "$4.99", per: "/mo", bullets: ["Everything in Free", "Tip Jar on your station", "Listener analytics", "Up to 5 stations"] },
        { name: "Broadcaster",    price: "$9.99", per: "/mo", bullets: ["Up to 25 stations", "AI DJ + AI-produced shows", "Embed widget on your site", "Priority support"], featured: true },
      ]
    : [
        { name: "On-Air Ad · 3-min",  price: "$35",  per: "/spot", bullets: ["Airs every 6 songs", "7 days worldwide", "Upload your own MP3"] },
        { name: "On-Air Ad · 5-min",  price: "$45",  per: "/spot", bullets: ["Same reach, more story", "Great for storytelling brands", "Admin-reviewed in <24h"], featured: true },
        { name: "AI Show + Ad bundle", price: "$60",  per: "/pkg", bullets: ["3-min AI-produced show", "7-day on-air sponsorship", "Save $15 vs separate"] },
      ];

  const CTA = isArtist
    ? { url: "fliboe.com", action: "Sign up free → launch in 5 minutes." }
    : { url: "fliboe.com/advertise", action: "Sponsor your first 7-day flight today." };

  // A5 portrait at ~150 DPI → 1240 x 1748 px (screenshots cleanly)
  return (
    <div
      style={{ width: 1240, height: 1748 }}
      className="bg-[#0a0a0a] text-white relative overflow-hidden mx-auto"
      data-testid={`brochure-${variant}`}
    >
      {/* gradient hero glow */}
      <div className={`absolute -top-32 -left-32 w-[600px] h-[600px] rounded-full bg-gradient-to-br ${HERO.accent} opacity-30 blur-[140px] pointer-events-none`} />
      <div className="absolute -bottom-32 -right-24 w-[500px] h-[500px] rounded-full bg-gradient-to-br from-fuchsia-500 to-sky-500 opacity-25 blur-[120px] pointer-events-none" />

      {/* Top bar */}
      <header className="flex items-center justify-between px-14 pt-10 relative z-10">
        <div className="flex items-baseline gap-2">
          <Radio className="w-7 h-7 text-emerald-400" />
          <div className="font-black text-3xl tracking-tighter">FLIBOE<span className="text-emerald-400">.</span></div>
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/50 ml-1">// ON AIR WORLDWIDE</div>
        </div>
        <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/45">
          {isArtist ? "FOR ARTISTS" : "FOR BUSINESSES"}
        </div>
      </header>

      {/* HERO */}
      <section className="px-14 mt-12 relative z-10">
        <div className="font-mono text-[10px] tracking-[0.4em] uppercase text-emerald-400 mb-3">// {HERO.eyebrow}</div>
        <h1 className="font-black tracking-tighter leading-[0.95] text-6xl">
          {HERO.title}
        </h1>
        <p className="text-white/70 text-lg mt-5 max-w-[820px] leading-relaxed">{HERO.sub}</p>
      </section>

      {/* CORE FEATURES */}
      <section className="px-14 mt-12 relative z-10">
        <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/50 mb-3">// What&apos;s included</div>
        <div className="grid grid-cols-3 gap-4">
          {PLATFORM_FEATURES.map((f) => (
            <div key={f.title} className="rounded-2xl bg-white/[0.04] border border-white/10 p-4">
              <f.icon className={`w-5 h-5 mb-2 ${isArtist ? "text-fuchsia-400" : "text-emerald-400"}`} />
              <div className="font-bold text-sm mb-1">{f.title}</div>
              <div className="text-white/60 text-[11px] leading-snug">{f.body}</div>
            </div>
          ))}
        </div>
      </section>

      {/* PULSE */}
      <section className="px-14 mt-10 relative z-10">
        <div className="rounded-2xl border border-fuchsia-400/30 bg-gradient-to-br from-fuchsia-500/10 to-emerald-500/10 p-6">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-5 h-5 text-fuchsia-400" />
            <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-fuchsia-300">// FLIBOE PULSE · AI-SEO BLOG</div>
          </div>
          <div className="grid grid-cols-12 gap-4 items-start">
            <div className="col-span-5">
              <h2 className="font-black text-2xl tracking-tight leading-tight">{PULSE_BLOCK.title}</h2>
              <p className="text-white/70 text-[12px] mt-2 leading-snug">{PULSE_BLOCK.body}</p>
              <div className="text-white/40 text-[10px] mt-3 italic">
                Licensed for your own websites only. No reselling.
              </div>
            </div>
            <div className="col-span-7 grid grid-cols-3 gap-3">
              {PULSE_TIERS.map((t) => (
                <div
                  key={t.name}
                  className={`rounded-xl p-3 ${t.featured ? "border-2 border-fuchsia-400 bg-fuchsia-500/10" : "border border-white/15 bg-white/[0.03]"}`}
                >
                  <div className="font-bold text-sm">{t.name}</div>
                  <div className="font-black text-3xl mt-1 leading-none">{t.price}<span className="text-white/40 text-xs font-normal">{t.per}</span></div>
                  <div className="text-[11px] text-white/75 mt-2 font-semibold">{t.line1}</div>
                  <div className="text-[11px] text-white/55">{t.line2}</div>
                  {t.featured && <div className="mt-2 inline-block text-[9px] tracking-widest uppercase font-bold text-fuchsia-300 bg-fuchsia-500/20 px-1.5 py-0.5 rounded">Most picked</div>}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PLANS */}
      <section className="px-14 mt-8 relative z-10">
        <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/50 mb-3">
          // {isArtist ? "Your plan" : "Your ad package"}
        </div>
        <div className="grid grid-cols-3 gap-4">
          {PLANS.map((p) => (
            <div
              key={p.name}
              className={`rounded-2xl p-5 ${p.featured ? "border-2 border-emerald-400 bg-emerald-500/8" : "border border-white/15 bg-white/[0.03]"}`}
            >
              <div className="font-bold text-base">{p.name}</div>
              <div className="font-black text-4xl mt-1 leading-none">{p.price}<span className="text-white/40 text-sm font-normal">{p.per}</span></div>
              <ul className="mt-3 space-y-1.5">
                {p.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-1.5 text-[11px] text-white/75">
                    <Check className={`w-3 h-3 mt-0.5 flex-shrink-0 ${p.featured ? "text-emerald-400" : "text-white/60"}`} />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* CTA + footer */}
      <section className="px-14 mt-9 relative z-10">
        <div className="flex items-end justify-between gap-6">
          <div>
            <div className="font-black text-3xl tracking-tight leading-tight">{CTA.action}</div>
            <div className="text-emerald-400 font-mono mt-1 text-sm tracking-widest">→ {CTA.url}</div>
          </div>
          <div className="text-right text-[10px] text-white/40 font-mono uppercase tracking-widest leading-snug max-w-[280px]">
            {isArtist
              ? "Built by DJ Anup. 20 years in ad agencies. Zero hardware. Zero cost beyond your tier."
              : "Direct line to real listeners. No bot traffic. No middleman. Fair pricing."}
          </div>
        </div>
        <div className="border-t border-white/10 mt-6 pt-3 flex items-center justify-between text-[10px] font-mono uppercase tracking-[0.25em] text-white/35">
          <span>FLIBOE.COM</span>
          <span>// {isArtist ? "FOR ARTISTS · v1" : "FOR BUSINESSES · v1"}</span>
          <span>A5 · 2026</span>
        </div>
      </section>
    </div>
  );
}
